import { useState } from "react";
import { Button, Text, IconButton } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { FiTrash2 } from "react-icons/fi";
import {
  DialogRoot,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { ChatService } from "@/client";
import useCustomToast from "@/hooks/useCustomToast";

const DeleteConversation = ({ id }: { id: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams({ from: "/_layout/chat/$convId" });
  const { showSuccessToast, showErrorToast } = useCustomToast();

  // Mutation for deleting a conversation
  const deleteMutation = useMutation({
    mutationFn: () => ChatService.deleteConversation({ id }),
    onSuccess: () => {
      showSuccessToast("대화가 성공적으로 삭제되었습니다.");
      queryClient.invalidateQueries({ queryKey: ["conversations"] });

      // 현재 페이지가 삭제된 대화인 경우 대화 목록으로 리다이렉션
      if (params.convId === id) {
        navigate({ to: "/conversations" });
      }

      setIsOpen(false);
    },
    onError: () => {
      showErrorToast("대화를 삭제하는 중 오류가 발생했습니다.");
    },
  });

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      role="alertdialog"
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open);
      }}
    >
      <DialogTrigger asChild>
        <IconButton
          aria-label="대화 삭제"
          size="sm"
          variant="ghost"
          color="red.500"
          _hover={{ bg: "red.50", color: "red.600" }}
          _active={{ bg: "red.100" }}
          onClick={(e) => e.stopPropagation()}
        >
          <FiTrash2 />
        </IconButton>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>대화 삭제</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text mb={4}>이 대화는 영구적으로 삭제됩니다. 계속하시겠습니까?</Text>
        </DialogBody>
        <DialogFooter gap={2}>
          <Button
            variant="subtle"
            colorPalette="gray"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          >
            취소
          </Button>
          <Button
            variant="solid"
            colorPalette="red"
            onClick={() => deleteMutation.mutate()}
            loading={deleteMutation.isPending}
          >
            삭제
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default DeleteConversation;
